import {
  Divider,
  Radio,
  RadioProps,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import aiReferenceIcon from '../../../assets/studio/rights/aiIcon.svg'
import attributionIcon from '../../../assets/studio/rights/attributionIcon.svg'
import comercialIcon from '../../../assets/studio/rights/commercialIcon.svg'
import nonAiReferenceIcon from '../../../assets/studio/rights/nonAiReferenceIcon.svg'
import nonCommercial from '../../../assets/studio/rights/nonCommercialIcon.svg'
import allRightsIcon from '../../../assets/studio/rights/rightsIcon.svg'
import { CommonRights, generatePermissions } from '../../../utils/utils'
import RootButton from '../../Buttons/RootButton'

export interface RadioAnswer {
  value: number
  response: string
  nextQuestionId: number
  icon: string
}

export interface RadioQuestion {
  id: number
  question: string
  answers: RadioAnswer[]
}

interface RightsQuestionProps extends RadioProps {
  rightsQuestion: RadioQuestion
  activeStep: number
  responseValue: string
  setResponseValue: (value: string) => void
  onClick: () => void
  onCLickAnswer: (event: any) => void
}

interface RightsIconsProps {
  rights: CommonRights
}

export const RightsIcons: React.FC<RightsIconsProps> = ({ rights }) => {
  const { t } = useTranslation()
  const icons = []
  if (!rights) return []
  if (rights.retainRights) {
    icons.push(
      <Tooltip
        key="retainRights"
        withArrow
        label={t('components.rights.allRights', 'Creator retains all rights')}
        position="bottom"
      >
        <img key="retainRights" src={allRightsIcon} alt="pass-through" />
      </Tooltip>
    )
  }
  if (rights.allowAI) {
    icons.push(
      <Tooltip
        key="allowAI"
        withArrow
        label={t('components.rights.aiReference', 'Approved for AI reference')}
        position="bottom"
      >
        <img key="allowAI" src={aiReferenceIcon} alt="commercial" />
      </Tooltip>
    )
  } else {
    icons.push(
      <Tooltip
        key="notAllowAI"
        withArrow
        label={t('components.rights.nonAiReference', 'Not Approved for AI reference')}
        position="bottom"
      >
        <img key="notAllowAI" src={nonAiReferenceIcon} alt="non-commercial" />
      </Tooltip>
    )
  }
  if (rights.allowCommercialUse) {
    icons.push(
      <Tooltip
        key="allowCommercialUse"
        withArrow
        label={t('components.rights.commercial', 'Commercial Use Allowed')}
        position="bottom"
      >
        <img key="allowCommercialUse" src={comercialIcon} alt="attribution" />
      </Tooltip>
    )
  } else {
    icons.push(
      <Tooltip
        key="notAllowCommercialUse"
        withArrow
        label={t('components.rights.nonCommercial', 'No Commercial Usage')}
        position="bottom"
      >
        <img key="notAllowCommercialUse" src={nonCommercial} alt="no-derivatives" />
      </Tooltip>
    )
  }
  if (rights.requireAttributions) {
    icons.push(
      <Tooltip
        key="requireAttributions"
        withArrow
        label={t('components.rights.attribution', 'Attribution')}
        position="bottom"
      >
        <img key="requireAttributions" src={attributionIcon} alt="derivatives" />
      </Tooltip>
    )
  }

  return <Stack style={{ flexDirection: 'row', gap: '5px' }}>{icons}</Stack>
}

const RightsQuestion: React.FC<RightsQuestionProps> = ({
  rightsQuestion,
  activeStep,
  responseValue,
  setResponseValue,
  onClick,
  onCLickAnswer,
  ...props
}) => {
  const { style } = props
  return (
    <Radio.Group
      key={rightsQuestion.id}
      style={{ ...style }}
      label={`${rightsQuestion.id}. ${rightsQuestion.question}`}
      onClick={onClick}
    >
      {activeStep === rightsQuestion.id &&
        rightsQuestion.answers.map(answer => (
          <Radio
            style={{ margin: '5px 0' }}
            key={`${rightsQuestion.id}-${answer.value}`}
            value={`${answer.value}`}
            label={answer.response}
            onClick={onCLickAnswer}
          />
        ))}
    </Radio.Group>
  )
}

interface RightsProps {
  activeStep: number
  setActiveStep: (value: number) => void
  responseValues: { [key: number]: string }
  setResponseValues: (value: { [key: number]: string }) => void
  setRights: (value: CommonRights) => void
  prevStep: () => void
  nextStep: () => void
  saveDraft: () => void
  metadata: any
}

const Rights: React.FC<RightsProps> = ({
  activeStep,
  setActiveStep,
  responseValues,
  setResponseValues,
  setRights,
  prevStep,
  nextStep,
  saveDraft,
  metadata,
}) => {
  const rightsData = metadata.rights
  const rightRules = rightsData.rules
  const rightQuestions = rightRules.questions
  const totalQuestions = rightQuestions.length
  const theme = useMantineTheme()
  const { t } = useTranslation()

  // Active Rights Step
  // -2 -> Not Answered
  // -1 -> Error
  // 0 -> Success
  // {1,2,3,...,n} -> Question {1,2,3,...,n}

  const [responseValue, setResponseValue] = useState<string>(responseValues[activeStep] || '-2')

  // function that determines which question is the last according to answer
  const lastQuestion = useCallback(
    (response: number) => {
      if (activeStep === 1 && response === 0) return true
      if (totalQuestions < activeStep && response !== -2) return true
      return false
    },
    [activeStep, totalQuestions]
  )

  const nextStepOnDone = useCallback(() => {
    setRights(generatePermissions(responseValues))
    nextStep()
    saveDraft()
  }, [responseValues, setRights, nextStep, saveDraft])

  // mark all the questions from the current active step to the last one as not answered, when going back
  useEffect(() => {
    const newResponseValues = { ...responseValues }
    for (let i = activeStep; i <= totalQuestions; i++) {
      newResponseValues[i] = '-2'
    }
    setResponseValues(newResponseValues)
  }, [activeStep])

  return (
    <Stack style={{ textAlign: 'left', gap: '14px' }}>
      <Divider />
      <Title order={3}>{t('components.studio.rights.title', 'Select Creator Rights')}</Title>
      <Trans i18nKey="components.studio.rights.description">
        <Text style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}>
          Answer the questions below to determine which creator rights you’d like to include in this
          item’s metadata. These rights are not yet enforceable, but will help determine how your
          item is used and attributed to you.
        </Text>
      </Trans>
      {rightQuestions?.map((question: RadioQuestion) => (
        <Stack key={question.id}>
          {activeStep >= question.id && (
            <>
              <RightsQuestion
                key={question.id}
                rightsQuestion={question}
                activeStep={activeStep}
                responseValue={responseValues[question.id] || responseValue}
                setResponseValue={setResponseValue}
                onClick={() => {
                  if (question.id < activeStep) {
                    setActiveStep(question.id)
                  }
                }}
                onCLickAnswer={event => {
                  event.stopPropagation()
                  setResponseValue(event.target.value)
                  setResponseValues({ ...responseValues, [activeStep]: `${event.target.value}` })
                  if (!lastQuestion(Number(event.target.value))) setActiveStep(activeStep + 1)
                }}
                style={{
                  div: {
                    opacity: question.id === activeStep ? 1 : 0.7,
                    cursor: question.id === activeStep ? 'default' : 'pointer',
                  },
                }}
              />
              <Divider />
            </>
          )}
        </Stack>
      ))}
      <Stack style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
        <RootButton secondary onClick={prevStep}>
          {t('buttons.previous')}
        </RootButton>
        <RootButton
          onClick={() => nextStepOnDone()}
          disabled={!lastQuestion(Number(responseValues[activeStep]))}
        >
          {t('buttons.continue')}
        </RootButton>
      </Stack>
      <Trans i18nKey="components.studio.rights.moreInfo" transWrapTextNodes>
        <Text style={{ textAlign: 'left', color: theme.colors.paragraph[0], marginBottom: '90px' }}>
          For more information, see our guide to{' '}
          <Link
            to="https://docs.lamina1.com/docs/Guidelines-Fair-Use--Creator-Rights-on-LAMINA1"
            target="_blank"
          >
            Fair Use & Creator Rights
          </Link>{' '}
          on LAMINA1.
        </Text>
      </Trans>
    </Stack>
  )
}

export default Rights
